import React, { useEffect, useState } from 'react';
import Input from './Input';
import Checkbox from './Checkbox';
import Combobox from './Combobox';
import Button from './Button';
import { useDeviceLayout } from './DeviceLayoutProvider';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Search, Minus, Plus, Inbox, Archive } from 'lucide-react';

const Table = ({
    headers,
    keyForPreview = [],
    data,
    setData,
    itemsPC = 10,
    itemsMobile = 5,
    clickableRow = false,
    archiveClickable = true,
    actionRow = () => { },
    needResearch = true,
    specialFilter = [],
    renderRowPC,
    renderRowMobile = null,
    classes = '',
    needCheckbox = false,
    viewCheckboxMobile = true,
    keyForCheck = 'ID',
    renderButtonForCheckbox = null,
    needAdd = false,
    addAction = () => { },
    needArchive = false,
    archiveKey = '',
    archiveDisequal = false,
    archiveValue = true,
    notFoundMessage = 'Nessun risultato trovato',
    forceSmall = false,
    needSortForSmall = true,
    needOnlyPreviewSmall = false,
    needSelectedGlowing = false,
    rowIndexSelected = null,
    forceXS = false,
}) => {
    const deviceLayout = useDeviceLayout();
    const isLittle = forceSmall ? true : deviceLayout.isLittle;
    const [keyForPreviewState, setKeyForPreviewState] = useState(keyForPreview);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [expandedCard, setExpandedCard] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    // useEffect(() => {
    //     console.log(headers, keyForPreview, headers.length, keyForPreview.length, headers.length === keyForPreview.length);
    // }, []);

    // Filtra i dati in base alla ricerca
    const filteredData = data.filter(item => {
        const row = item.value || {};

        // Filtro archiviati
        const archiveFilter = needArchive
            ? showArchived
                ? archiveDisequal
                    ? row[archiveKey] !== archiveValue
                    : row[archiveKey] === archiveValue
                : archiveDisequal
                    ? row[archiveKey] === archiveValue
                    : row[archiveKey] !== archiveValue
            : true;

        // Filtro speciale: confronta il searchTerm con la label visualizzata
        const specialMatch = specialFilter?.some(special => {
            const rowValue = row[special.key];

            // Trova il valore corrispondente
            const match = special.values.find(v => v.value === rowValue);

            // Verifica se la label contiene il searchTerm
            return match && match.label.toLowerCase().includes(searchTerm.toLowerCase());
        });

        // Filtro generico: cerca in tutti gli altri valori tranne quelli gestiti da specialFilter
        const generalSearchMatch = Object.entries(row).some(([key, value]) => {
            const isSpecial = specialFilter?.some(s => s.key === key);
            if (isSpecial) return false;

            return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });

        const searchFilter = specialMatch || generalSearchMatch;

        return archiveFilter && searchFilter;
    });

    // Ordina i dati
    const sortedData = [...filteredData].sort((a, b) => {
        if (sortConfig.key !== null) {
            const aValue = a.value[sortConfig.key];
            const bValue = b.value[sortConfig.key];

            // Gestione di valori null/undefined
            if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

            // Tentativo di parsing come numero
            const aNumber = parseFloat(aValue);
            const bNumber = parseFloat(bValue);
            const isANumber = !isNaN(aNumber);
            const isBNumber = !isNaN(bNumber);

            // Tentativo di parsing come data
            const aDate = parseDate(aValue);
            const bDate = parseDate(bValue);
            const isADate = aDate !== null;
            const isBDate = bDate !== null;

            // Se entrambi sono date, confronta come date
            if (isADate && isBDate) {
                return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
            }

            // Se entrambi sono numeri, confronta come numeri
            if (isANumber && isBNumber) {
                return sortConfig.direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
            }

            // Se uno è data e l'altro no, le date vengono prima (o dopo in base alla direzione)
            if (isADate !== isBDate) {
                return sortConfig.direction === 'asc'
                    ? (isADate ? -1 : 1)
                    : (isADate ? 1 : -1);
            }

            // Confronto come stringhe per tutti gli altri casi
            const aString = String(aValue).toLowerCase();
            const bString = String(bValue).toLowerCase();

            if (aString < bString) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aString > bString) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
        }
        return 0;
    });

    // Funzione helper per il parsing delle date
    function parseDate(value) {
        if (!value) return null;

        const stringValue = String(value).trim();

        // Regex per i formati supportati
        const dateTimeRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/;
        const match = stringValue.match(dateTimeRegex);

        if (!match) return null;

        const [, day, month, year, hours = '0', minutes = '0', seconds = '0'] = match;

        // Crea la data (mesi sono 0-based in JavaScript)
        const date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
        );

        // Verifica che la data sia valida
        return date instanceof Date && !isNaN(date) ? date.getTime() : null;
    }

    // Calcola i dati per la pagina corrente
    const totalPages = Math.ceil(sortedData.length / (isLittle ? itemsMobile : itemsPC));
    const startIndex = (currentPage - 1) * (isLittle ? itemsMobile : itemsPC);
    const endIndex = startIndex + (isLittle ? itemsMobile : itemsPC);
    const currentData = sortedData.slice(startIndex, endIndex);
    const allChecked = sortedData.length > 0 && sortedData.every(item => item.checked);

    const hasChecked = data.some(item => item.checked);

    // Funzione per cambiare lo stato di una singola riga
    const handleRowCheckboxChange = (id) => {
        setData(prevData =>
            prevData.map(item =>
                item.value[keyForCheck] === id ? { ...item, checked: !item.checked } : item
            )
        );
    };

    // Funzione per selezionare/deselezionare tutte le righe
    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setData(prevData =>
            prevData.map(item =>
                sortedData.some(sortedItem => sortedItem.value[keyForCheck] === item.value[keyForCheck])
                    ? { ...item, checked: isChecked }
                    : item
            )
        );
    };

    // Funzione per richiedere l'ordinamento
    const requestSort = (k) => {
        let direction = 'asc';
        if (sortConfig.key === k && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: k, direction });
        setKeyForPreviewState(k ? [k, ...keyForPreview.filter(key => key !== k)] : keyForPreview);
        setCurrentPage(1);
    };

    // Funzioni per cambiare pagina
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const toggleExpandCard = (index, e) => {
        e.stopPropagation();
        setExpandedCard(expandedCard === index ? null : index);
    };

    const handleCardClick = (row, rowIndex) => {
        if ((!showArchived && clickableRow) || (showArchived && archiveClickable)) {
            actionRow(row, rowIndex, showArchived);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e);
        setCurrentPage(1);
    };

    const toggleArchiveFilter = () => {
        setShowArchived(!showArchived);
        setCurrentPage(1);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    return (
        <div className={`w-full ${classes}`}>
            {/* Barra di ricerca */}
            {needResearch && (
                <div className="mb-4 flex items-center">
                    <div className="flex items-center pr-2 pointer-events-none">
                        <Search size={20} className="text-gray-500 dark:text-gray-300" />
                    </div>
                    <Input
                        label="Cerca"
                        value={searchTerm}
                        setValue={handleSearch}
                        type="text"
                        classes="w-full"
                    />
                    {needArchive && (
                        <Button type='icon' classes='ml-2' onClick={toggleArchiveFilter} >
                            {showArchived ? (
                                <Inbox size={20} />
                            ) : (
                                <Archive size={20} />
                            )}
                        </Button>
                    )}
                    {needAdd && (
                        <Button type='full' onClick={addAction} classes='ml-2'>
                            AGGIUNGI <Plus size={20} strokeWidth={2} className="inline-flex ms-1" />
                        </Button>
                    )}
                </div>
            )}
            {
                isLittle ? (
                    <div>
                        {/* Intestazione con Combobox per l'ordinamento */}
                        <div className="flex justify-between items-center mb-2">
                            {needCheckbox ? (
                                <Checkbox
                                    checked={allChecked}
                                    onChange={handleSelectAll}
                                    size={18}
                                />
                            ) : (
                                <div></div>
                            )}
                            {
                                needCheckbox && hasChecked && (
                                    <div className="text-sm">
                                        {renderButtonForCheckbox()}
                                    </div>
                                )
                            }

                            {
                                needSortForSmall && (
                                    <Combobox
                                        options={
                                            headers.map(header => ({
                                                value: header.key,
                                                label: `${header.label} ${sortConfig.key === header.key ?
                                                    (sortConfig.direction === 'asc' ? '(↑)' : '(↓)') : ''}`
                                            }))
                                        }
                                        label={"Ordina per ..."}
                                        onChange={requestSort}
                                        classes='w-[50%]'
                                    // defaultValue={sortConfig.key + (sortConfig.direction === 'asc' ? ' (↑)' : ' (↓)')}
                                    />
                                )
                            }
                        </div>

                        {/* Corpo della tabella come card */}
                        <div className={`space-y-2`}>
                            {currentData.length > 0 ? (
                                currentData.map((row, rowIndex) => (
                                    <div
                                        key={`mobile-row-${rowIndex}`}
                                        onClick={() => handleCardClick(row, rowIndex)}
                                        className={`${needSelectedGlowing && rowIndex === rowIndexSelected ? 'bg-blue-200 dark:bg-blue-800' : 'bg-gray-200 dark:bg-neutral-800 '} rounded-lg shadow break-all ${((!showArchived && clickableRow) || (showArchived && archiveClickable)) && 'hover:cursor-pointer hover:bg-gray-300 dark:hover:bg-neutral-700'}`}
                                    >
                                        <div className={`${forceXS ? 'p-2' : 'p-3'}`}>
                                            <div className="flex justify-between items-start">
                                                {needCheckbox && viewCheckboxMobile && (
                                                    <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                                                        <Checkbox
                                                            checked={row.checked || false}
                                                            onChange={(e) => {
                                                                handleRowCheckboxChange(row.value[keyForCheck]);
                                                            }}
                                                            size={18}
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                )}

                                                <div className={`flex-1 ${forceXS && 'min-w-0'}`}>
                                                    {
                                                        keyForPreviewState.map((key, i) => (
                                                            <div key={`mobile-preview-${key}`} className={`${i !== keyForPreviewState.length - 1 && 'mb-1'} ${forceXS && 'flex items-start'}`}>
                                                                <span className={`font-medium text-sm text-gray-500 dark:text-gray-400 ${forceXS && 'flex-shrink-0'}`}>
                                                                    {headers.find(header => header.key === key)?.label}:
                                                                </span>
                                                                <span className={`ml-1 text-sm ${forceXS && 'truncate flex-1 min-w-0'}`}>
                                                                    {row.value[key]}
                                                                </span>
                                                            </div>
                                                        ))
                                                    }
                                                </div>

                                                {
                                                    headers.length === keyForPreviewState.length || needOnlyPreviewSmall ? (
                                                        <></>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => toggleExpandCard(rowIndex, e)}
                                                            className="text-blue-600 dark:text-blue-400 p-1"
                                                        >
                                                            {expandedCard === rowIndex ? (
                                                                <ChevronUp size={20} />
                                                            ) : (
                                                                <ChevronDown size={20} />
                                                            )}
                                                        </button>
                                                    )}
                                            </div>

                                            {/* Contenuto espanso */}
                                            {expandedCard === rowIndex && headers.length !== keyForPreviewState.length && (
                                                <div
                                                    className="mt-3 pt-3 border-t border-gray-300 dark:border-neutral-700"
                                                    onClick={() => handleCardClick(row, rowIndex)}
                                                >
                                                    {renderRowMobile({
                                                        rowData: row,
                                                        headers: headers,
                                                        keyForPreview: keyForPreviewState
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex justify-center items-center py-4">
                                    {notFoundMessage}
                                </div>
                            )}
                        </div>

                        {/* Paginazione semplificata */}
                        {sortedData.length > 0 && totalPages > 1 && (
                            <nav className="mt-4 flex items-center justify-between">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`flex items-center justify-center p-2 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-neutral-800 dark:hover:bg-neutral-700 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <ChevronLeft size={20} />
                                </button>

                                <span className="text-sm">
                                    Pagina {currentPage} di {totalPages}
                                </span>

                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`flex items-center justify-center p-2 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-neutral-800 dark:hover:bg-neutral-700 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </nav>
                        )}
                    </div>
                ) : (
                    <div>
                        {/* Intestazioni */}
                        <div div className='flex flex-row'>
                            {
                                needCheckbox &&
                                <Checkbox
                                    checked={allChecked}
                                    onChange={handleSelectAll}
                                    size={18}
                                />
                            }
                            <div className={`grid text-xs text-blue-600 py-4 border-b-2 border-gray-300 dark:border-neutral-700 w-full`}
                                style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}>
                                {headers.map((header) => (
                                    header.key !== '' ? (
                                        <div
                                            key={`header-${header.key}`}
                                            className='flex items-center hover:cursor-pointer pl-2 font-bold '
                                            onClick={() => requestSort(header.key)}
                                        >
                                            {header.label}
                                            {sortConfig.key === header.key ? (
                                                <span className="ml-1">
                                                    {sortConfig.direction === 'asc' ? (
                                                        <ChevronUp size={16} strokeWidth={3} />
                                                    ) : (
                                                        <ChevronDown size={16} strokeWidth={3} />
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="ml-1">
                                                    <Minus size={16} strokeWidth={3} />
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div key={`header-${header.key}`} className='flex items-center pl-2 font-bold hover:cursor-default'>
                                            {header.label}
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>

                        {/* Corpo della tabella */}
                        <div className='rounded-b-lg'>
                            {currentData.length > 0 ? (
                                currentData.map((row, rowIndex) => (
                                    <div
                                        key={`row-${rowIndex}`}
                                        className={`${((!showArchived && clickableRow) || (showArchived && archiveClickable)) && 'hover:cursor-pointer hover:bg-gray-200 dark:hover:bg-neutral-800'} break-all ${showArchived && 'text-black/50 dark:text-white/50'}`}
                                        onClick={(e) => {
                                            if (!e.target.closest('.no-row-action')) {
                                                ((!showArchived && clickableRow) || (showArchived && archiveClickable)) && actionRow(row, rowIndex, showArchived);
                                            }
                                        }}
                                    >
                                        {renderRowPC({ rowData: row, handleRowCheckboxChange })}
                                    </div>
                                ))
                            ) : (
                                <div className="flex justify-center items-center py-4">
                                    {notFoundMessage}
                                </div>
                            )}
                        </div>

                        {/* Paginazione */}
                        {
                            sortedData.length > 0 && totalPages > 1 && (
                                <nav className="mt-2 flex items-center justify-center">
                                    <ul className="inline-flex text-base h-10">
                                        <li>
                                            <button
                                                onClick={() => goToPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className={`flex items-center justify-center px-2 h-10 ms-0 rounded-s-lg bg-gray-200 hover:bg-gray-300 dark:bg-neutral-800 dark:hover:bg-neutral-700  ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                                    }`}
                                            >
                                                <ChevronLeft size={20} />
                                            </button>
                                        </li>

                                        {getPageNumbers().map((page) => (
                                            <li key={`page-${page}`}>
                                                <button
                                                    onClick={() => goToPage(page)}
                                                    className={`flex items-center px-4 h-10 hover:cursor-pointer ${currentPage === page
                                                        ? 'bg-blue-200 hover:bg-blue-300 dark:bg-gray-700 hover:dark:bg-gray-600 text-blue-600'
                                                        : 'bg-gray-200 hover:bg-gray-300 dark:bg-neutral-800 dark:hover:bg-neutral-700'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        ))}

                                        <li>
                                            <button
                                                onClick={() => goToPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className={`flex items-center justify-center px-2 h-10 rounded-e-lg bg-gray-200 hover:bg-gray-300 dark:bg-neutral-800 dark:hover:bg-neutral-700 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                                    }`}
                                            >
                                                <ChevronRight size={20} />
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )
                        }
                        {
                            needCheckbox && hasChecked && (
                                <div className="flex justify-center mt-4">
                                    {renderButtonForCheckbox()}
                                </div>
                            )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default Table;